import DriverLicenceFields, { validDriverLicence } from './DriverLicenceFields';
import { useState, useEffect } from 'react';
import { identityCheckConsentCheckboxText } from 'src/settings/text';
import DateOfBirth from 'src/design/components/dateOfBirth/DateOfBirth';
import Divider from 'src/design/components/divider/Divider';
import FormCheckbox from 'src/design/components/formCheckbox/FormCheckbox';
import HelpDisclaimer from 'src/design/components/helpDisclaimer/HelpDisclaimer';
import InfoBox from 'src/design/components/infoBox/InfoBox';
import InputError from 'src/design/components/inputError/InputError';
import OnboardingNextButton from 'src/components/oldOnboarding/common/onboardingNextButton/OnboardingNextButton';
import OnboardingScreen, {
  OnboardingScreenHeader,
  OnboardingScreenContent,
  OnboardingFormLabel,
} from 'src/components/oldOnboarding/common/onboardingScreen/OnboardingScreen';
import Paragraph from 'src/design/components/bodyText/Paragraph';
import RadioSelect from 'src/design/components/radioSelect/RadioSelect';
import ResidentialAddress from 'src/components/oldOnboarding/identityVerification/ResidentialAddress';
import Spacing from 'src/design/styleguide/spacing/Spacing';
import TextInputWithError from 'src/design/components/textInput/TextInputWithError';
import AdvisorOnboarding from 'src/components/onboarding/advisor/AdvisorOnboarding';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import moment from 'moment';
import _ from 'lodash';

const usCitizenFormValueToFieldValue = (formValue) => {
  if (formValue === true) return 'yes';
  if (formValue === false) return 'no';
  return '';
};

const usCitizenFieldValueToFormValue = (fieldValue) => {
  if (fieldValue === 'yes') return true;
  if (fieldValue === 'no') return false;
};

const submitAllowed = (values) =>
  validDriverLicence(values) &&
  values.givenName &&
  values.familyName &&
  values.address &&
  values.mobilePhone &&
  values.dayOfBirth &&
  values.monthOfBirth &&
  values.yearOfBirth &&
  usCitizenFormValueToFieldValue(values.usCitizen) &&
  values.tsAndCsAgreed &&
  !values.isWholesale;

const EnterDetailsAsTheyAppear = (props) => (
  <InfoBox {...props}>
    <Paragraph>
      Please enter details exactly as they appear on your ID.
    </Paragraph>
  </InfoBox>
);

const IdentityVerificationFormAdvisor = ({
  onSubmit,
  onChange,
  values,
  identityVerificationFormErrors,
  identityVerificationFormErrorsCleared,
  submitting,
}) => {
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const setAdvisorDetails = (advisorDetails) => {
    onChange({ advisorDetails });
  };

  const selfValidateForm = () => {
    setErrors({});
    let newErrors = {};
    const dob = `${values.yearOfBirth}-${values.monthOfBirth}-${values.dayOfBirth}`;
    const age = moment().diff(moment(dob), 'years');
    if (age < 18) {
      newErrors.dob = 'You must be at least 18 years old';
    }
    // phone number
    if (!/^04\d{8}$/.test(values.mobilePhone)) {
      newErrors.mobilePhone =
        'Please enter a valid Australian mobile number which should start with 04 and contain 10 digits without spaces';
    }
    setErrors(newErrors);
    if (_.isEmpty(newErrors)) setStep(1);
    if (!_.isEmpty(newErrors)) window.scrollTo(0, 0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const Common = {
    Title: () => (
      <h1>
        <strong>
          <span style={{ color: 'var(--bx-color---purple-600)' }}>
            BrickX Adviser
          </span>
          <br />
        </strong>
      </h1>
    ),
  };

  if (step === 0) {
    return (
      <OnboardingScreen testRef="identity-verification-screen">
        <OnboardingScreenHeader>
          <Common.Title />
          <Spacing bottom="large">
            <h2>Step 1: Please tell us a bit about yourself</h2>
          </Spacing>
          <Spacing bottom="large">
            <Paragraph>
              We are required by Australian law to verify all BrickX advisers
            </Paragraph>
          </Spacing>
        </OnboardingScreenHeader>
        <OnboardingScreenContent>
          <form>
            <Spacing bottom="large">
              <EnterDetailsAsTheyAppear />
            </Spacing>
            <OnboardingFormLabel>First Name</OnboardingFormLabel>
            <Spacing bottom="normal">
              <TextInputWithError
                value={values.givenName}
                onChange={(value) => {
                  identityVerificationFormErrorsCleared('givenName');
                  onChange({ givenName: value });
                }}
                placeholder="John"
                error={
                  identityVerificationFormErrors.givenName &&
                  'Please enter a valid first name (alphabets only)'
                }
                testRef="given-name"
                errorTestRef="given-name-error"
              />
            </Spacing>
            <OnboardingFormLabel>Middle Name (Optional)</OnboardingFormLabel>
            <Spacing bottom="normal">
              <TextInputWithError
                value={values.middleName}
                onChange={(value) => {
                  identityVerificationFormErrorsCleared('middleName');
                  onChange({ middleName: value });
                }}
                placeholder="William"
                error={
                  identityVerificationFormErrors.middleName &&
                  'Please enter a valid first name (alphabets only)'
                }
                testRef="middle-name"
                errorTestRef="middle-name-error"
              />
            </Spacing>
            <OnboardingFormLabel>Last Name</OnboardingFormLabel>
            <Spacing bottom="normal">
              <TextInputWithError
                value={values.familyName}
                onChange={(value) => {
                  identityVerificationFormErrorsCleared('familyName');
                  onChange({ familyName: value });
                }}
                placeholder="Smith"
                error={
                  identityVerificationFormErrors.familyName &&
                  'Please enter a valid last name (alphabets only)'
                }
                testRef="family-name"
                errorTestRef="family-name-error"
              />
            </Spacing>
            <ResidentialAddress
              value={values.address}
              harmonyValue={values.harmonyAddressObject}
              onChange={(value) => {
                identityVerificationFormErrorsCleared('address');
                onChange({
                  address: value,
                  harmonyAddressObject: undefined,
                });
              }}
              onSuggestionSelect={(harmonyAddressObject) => {
                onChange({
                  address: harmonyAddressObject.fullAddress,
                  harmonyAddressObject,
                });
              }}
              showError={identityVerificationFormErrors.address}
            />
            <OnboardingFormLabel>Mobile</OnboardingFormLabel>
            <Spacing bottom="normal">
              <TextInputWithError
                value={values.mobilePhone}
                onChange={(value) => {
                  identityVerificationFormErrorsCleared('mobilePhone');
                  onChange({ mobilePhone: value });
                }}
                placeholder="e.g. 04xxxxxxxx"
                error={
                  errors.mobilePhone
                    ? errors.mobilePhone
                    : identityVerificationFormErrors.mobilePhone &&
                      'Please enter a valid Australian mobile number'
                }
                testRef="phone-number"
                errorTestRef="phone-number-error"
              />
            </Spacing>
            <OnboardingFormLabel>Date of Birth</OnboardingFormLabel>
            <Spacing bottom="normal">
              <DateOfBirth
                day={{
                  value: values.dayOfBirth,
                  onChange: (value) => {
                    identityVerificationFormErrorsCleared('dateOfBirth');
                    onChange({ dayOfBirth: value });
                  },
                  testRef: 'day-of-birth-select',
                }}
                month={{
                  value: values.monthOfBirth,
                  onChange: (value) => {
                    identityVerificationFormErrorsCleared('dateOfBirth');
                    onChange({ monthOfBirth: value });
                  },
                  testRef: 'month-of-birth-select',
                }}
                year={{
                  value: values.yearOfBirth,
                  onChange: (value) => {
                    identityVerificationFormErrorsCleared('dateOfBirth');
                    onChange({ yearOfBirth: value });
                  },
                  testRef: 'year-of-birth-select',
                }}
                error={
                  errors.dob
                    ? errors.dob
                    : identityVerificationFormErrors.dateOfBirth &&
                      'You must be at least 18 years old to register'
                }
                errorTestRef="date-of-birth-error"
              />
            </Spacing>
            <DriverLicenceFields
              values={values}
              onChange={onChange}
              errors={identityVerificationFormErrors}
              clearError={identityVerificationFormErrorsCleared}
            />
            <Divider top="normal" bottom="normal" />

            {/* ================ US CITIZEN ================ */}

            <OnboardingFormLabel>Are you a US citizen?</OnboardingFormLabel>
            <Spacing bottom="x-small">
              <RadioSelect
                name="us-citizen"
                value={usCitizenFormValueToFieldValue(values.usCitizen)}
                onChange={(value) =>
                  onChange({ usCitizen: usCitizenFieldValueToFormValue(value) })
                }
                options={[
                  { value: 'yes', labelText: 'Yes' },
                  { value: 'no', labelText: 'No' },
                ]}
                testRef="us-citizen"
              />
            </Spacing>
            <HelpDisclaimer>
              Your answer does not affect your eligibility to invest, and is for
              reporting purposes only.
            </HelpDisclaimer>
            <Divider top="normal" bottom="normal" />
            <FormCheckbox
              value={values.tsAndCsAgreed}
              onChange={(value) => onChange({ tsAndCsAgreed: value })}
              testRef="ts-and-cs-checkbox"
              labelTestRef="ts-and-cs-checkbox-label"
            >
              {identityCheckConsentCheckboxText()}
            </FormCheckbox>
            <OnboardingNextButton
              testRef="next-button"
              disabled={!submitAllowed(values) || submitting}
              color={PrimaryButton.colors.PURPLE}
              onClick={(e) => {
                e.preventDefault();
                selfValidateForm();
              }}
            />
            {identityVerificationFormErrors.requestError ? (
              <Spacing top="x-small">
                <InputError error="We are having some trouble contacting our verification service at the moment. Please try again later." />
              </Spacing>
            ) : null}
          </form>
        </OnboardingScreenContent>
      </OnboardingScreen>
    );
  }
  if (step === 1) {
    return (
      <OnboardingScreen testRef="identity-verification-screen">
        <OnboardingScreenHeader>
          <Common.Title />
          <Spacing bottom="large">
            <h2>Step 2: Please provide your Adviser details</h2>
          </Spacing>
        </OnboardingScreenHeader>
        <OnboardingScreenContent>
          <AdvisorOnboarding
            userData={values}
            setAdvisorDetails={setAdvisorDetails}
            onSubmit={onSubmit}
            identityVerificationFormErrors={identityVerificationFormErrors}
          />
        </OnboardingScreenContent>
      </OnboardingScreen>
    );
  }
};

export default IdentityVerificationFormAdvisor;
